import React, { useEffect, useRef, useState, useCallback } from 'react';
import Quagga from '@ericblade/quagga2';
import { Camera, X } from 'lucide-react';

const BarcodeScanner = ({ onDetected, onClose, isOpen }) => {
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const lastCodeRef = useRef(null);
  const lastTimeRef = useRef(0);
  const processingRef = useRef(false);

  const stopScanner = useCallback(() => {
    try {
      Quagga.stop();
      Quagga.offDetected(handleDetection);
    } catch (err) {
      // Ignorar errores al detener si no estaba corriendo
      console.warn('Error al detener Quagga:', err);
    }
  }, []);

  const handleDetection = useCallback((result) => {
    if (!result || !result.codeResult || !result.codeResult.code) return;
    
    const code = result.codeResult.code;
    const now = Date.now();

    // Prevenir detecciones múltiples muy rápidas (debounce de 1.5s)
    if (processingRef.current || (now - lastTimeRef.current < 1500)) {
      return;
    }

    if (code.length >= 4) { // Aceptamos códigos de al menos 4 dígitos
      processingRef.current = true;
      lastTimeRef.current = now;
      lastCodeRef.current = code;
      
      // Vibrar si es posible
      if (navigator.vibrate) {
        navigator.vibrate(200);
      }

      onDetected(code);
      // No detenemos el escáner inmediatamente aquí, dejamos que el padre decida (cerrando el modal)
      // O si queremos cerrar:
      // stopScanner(); 
    }
  }, [onDetected]);

  useEffect(() => {
    if (!isOpen || !scannerRef.current) return;

    let mounted = true;
    processingRef.current = false;
    lastTimeRef.current = 0;

    const initScanner = async () => {
      try {
        await new Promise((resolve, reject) => {
          Quagga.init(
            {
              inputStream: {
                name: 'Live',
                type: 'LiveStream',
                target: scannerRef.current,
                constraints: {
                  width: { min: 640 },
                  height: { min: 480 },
                  facingMode: 'environment',
                  aspectRatio: { min: 1, max: 2 }
                },
              },
              locator: {
                patchSize: "medium",
                halfSample: true,
              },
              numOfWorkers: 2,
              decoder: {
                readers: [
                  'code_128_reader',
                  'ean_reader',
                  'ean_8_reader',
                  'code_39_reader',
                  'upc_reader',
                ],
              },
              locate: true,
            },
            (err) => {
              if (err) reject(err);
              else resolve();
            }
          );
        });

        if (mounted) {
          Quagga.start();
          Quagga.onDetected(handleDetection);
        } else {
          // Si se desmontó mientras iniciaba
          Quagga.stop();
        }
      } catch (err) {
        console.error('Error iniciando escáner:', err);
        if (mounted) {
          setError('No se pudo acceder a la cámara. Verifica permisos.');
        }
      }
    };

    // Pequeño timeout para asegurar que el DOM está listo y evitar conflictos con streams previos
    const timeoutId = setTimeout(initScanner, 100);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      stopScanner();
    };
  }, [isOpen, stopScanner, handleDetection]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 max-w-lg w-full mx-4 flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Camera className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-bold">Escanear Código</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error ? (
          <div className="text-red-600 text-center p-8 bg-red-50 rounded-lg">
            <p>{error}</p>
            <button onClick={onClose} className="mt-4 btn-secondary">Cerrar</button>
          </div>
        ) : (
          <div className="relative flex-1 bg-black rounded-lg overflow-hidden min-h-[300px]">
            <div
              ref={scannerRef}
              className="w-full h-full absolute inset-0 [&>video]:w-full [&>video]:h-full [&>video]:object-cover"
            />
            {/* Guías visuales */}
            <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
              <div className="w-64 h-40 border-2 border-primary-500 rounded-lg relative shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
                <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-red-500 opacity-50 animate-pulse" />
              </div>
              <p className="text-white text-sm mt-4 drop-shadow-md font-medium">
                Apunta al código de barras
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarcodeScanner;
